import React, { useState, useMemo } from 'react';
import { usePayments, useCompletedJobs, useCreatePaymentRequest } from '../../hooks/usePaymentQueries';
import { useCompletedJobs as useAdminCompletedJobs } from '../../hooks/useAdmin';
import PaymentStatusBadge from '../../components/payment/PaymentStatusBadge';
import PaymentDetails from '../../components/payment/PaymentDetails';
import AdminPaymentActions from '../../components/payment/AdminPaymentActions';
import Button from '../../components/common/Button';
import { format } from 'date-fns';
import './AdminPayments.css';

const AdminPayments = () => {
  const [filters, setFilters] = useState({
    employer_payment_status: 'all',
    employee_payout_status: 'all',
    job_posting_id: '',
    date_from: '',
    date_to: ''
  });

  const [expandedPaymentId, setExpandedPaymentId] = useState(null);
  const [showCompletedJobs, setShowCompletedJobs] = useState(false);

  const { data: payments, isLoading: isLoadingPayments } = usePayments(filters);
  const { data: completedJobs, isLoading: isLoadingJobs } = useAdminCompletedJobs();
  const createPaymentRequestMutation = useCreatePaymentRequest();

  // Calculate statistics
  const stats = useMemo(() => {
    if (!payments) return { total: 0, pending: 0, totalAmount: 0, paidAmount: 0 };

    const total = payments.length;
    const pending = payments.filter(
      p => p.employer_payment_status === 'pending_employer_payment' ||
           p.employee_payout_status === 'pending_employee_payout'
    ).length;
    const totalAmount = payments.reduce((sum, p) => sum + (parseFloat(p.total_amount) || 0), 0);
    const paidAmount = payments
      .filter(p => p.employer_payment_status === 'employer_paid' && p.employee_payout_status === 'employee_paid')
      .reduce((sum, p) => sum + (parseFloat(p.total_amount) || 0), 0);

    return { total, pending, totalAmount, paidAmount };
  }, [payments]);

  // Get completed jobs without payment requests
  const jobsWithoutPayment = useMemo(() => {
    if (!completedJobs || !payments) return [];
    
    const paymentJobIds = new Set(payments.map(p => p.job_posting_detail?.id || p.job_posting || p.job_posting_id).filter(Boolean));
    return completedJobs.filter(job => !paymentJobIds.has(job.id));
  }, [completedJobs, payments]);

  const formatCurrency = (amount) => {
    if (!amount) return '£0.00';
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP'
    }).format(amount);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
      return format(new Date(dateStr), 'dd MMM yyyy');
    } catch {
      return dateStr;
    }
  };

  const handleCreatePaymentRequest = async (jobId) => {
    try {
      await createPaymentRequestMutation.mutateAsync(jobId);
    } catch (error) {
      // Error handled by mutation hook
    }
  };

  const handleToggleExpand = (paymentId) => {
    setExpandedPaymentId(expandedPaymentId === paymentId ? null : paymentId);
  };

  const handleResetFilters = () => {
    setFilters({
      employer_payment_status: 'all',
      employee_payout_status: 'all',
      job_posting_id: '',
      date_from: '',
      date_to: ''
    });
  };

  const hasActiveFilters = useMemo(() => {
    return filters.employer_payment_status !== 'all' ||
           filters.employee_payout_status !== 'all' ||
           filters.job_posting_id !== '' ||
           filters.date_from !== '' ||
           filters.date_to !== '';
  }, [filters]);

  if (isLoadingPayments) {
    return (
      <div className="admin-payments__loading">
        <div className="admin-payments__spinner"></div>
        <p>Loading payments...</p>
      </div>
    );
  }

  return (
    <div className="admin-payments">
      <div className="admin-payments__header">
        <h1 className="admin-payments__title">Payment Management</h1>
        <div className="admin-payments__header-actions">
          <Button
            variant="outline"
            size="medium"
            onClick={() => setShowCompletedJobs(!showCompletedJobs)}
          >
            {showCompletedJobs ? 'Hide' : 'Show'} Completed Jobs ({jobsWithoutPayment.length})
          </Button>
        </div>
      </div>

      {/* Statistics */}
      <div className="admin-payments__stats">
        <div className="admin-payments__stat-card">
          <div className="admin-payments__stat-label">Total Payments</div>
          <div className="admin-payments__stat-value">{stats.total}</div>
        </div>
        <div className="admin-payments__stat-card admin-payments__stat-card--highlight">
          <div className="admin-payments__stat-label">Pending Payments</div>
          <div className="admin-payments__stat-value">{stats.pending}</div>
        </div>
        <div className="admin-payments__stat-card">
          <div className="admin-payments__stat-label">Total Amount</div>
          <div className="admin-payments__stat-value">{formatCurrency(stats.totalAmount)}</div>
        </div>
        <div className="admin-payments__stat-card admin-payments__stat-card--highlight">
          <div className="admin-payments__stat-label">Paid Amount</div>
          <div className="admin-payments__stat-value">{formatCurrency(stats.paidAmount)}</div>
        </div>
      </div>

      {/* Filters */}
      <div className="admin-payments__filters-section">
        <h3 className="admin-payments__filters-title">Filter Payments</h3>
        <div className="admin-payments__filters">
          <div className="admin-payments__filter-group">
            <label htmlFor="employer-status" className="admin-payments__filter-label">
              Employer Payment Status
            </label>
            <select
              id="employer-status"
              value={filters.employer_payment_status}
              onChange={(e) => setFilters({ ...filters, employer_payment_status: e.target.value })}
              className="admin-payments__filter"
            >
              <option value="all">All Statuses</option>
              <option value="pending_employer_payment">Pending from Employer</option>
              <option value="employer_paid">Paid by Employer</option>
            </select>
          </div>

          <div className="admin-payments__filter-group">
            <label htmlFor="employee-status" className="admin-payments__filter-label">
              Employee Payout Status
            </label>
            <select
              id="employee-status"
              value={filters.employee_payout_status}
              onChange={(e) => setFilters({ ...filters, employee_payout_status: e.target.value })}
              className="admin-payments__filter"
            >
              <option value="all">All Statuses</option>
              <option value="pending_employee_payout">Pending Payout</option>
              <option value="employee_paid">Paid to Employee</option>
            </select>
          </div>

          <div className="admin-payments__filter-group">
            <label htmlFor="job-posting-id" className="admin-payments__filter-label">
              Job Posting ID
            </label>
            <input
              id="job-posting-id"
              type="text"
              placeholder="Enter job posting ID (e.g., 123)"
              value={filters.job_posting_id}
              onChange={(e) => setFilters({ ...filters, job_posting_id: e.target.value })}
              className="admin-payments__filter admin-payments__filter--text"
            />
          </div>

          <div className="admin-payments__filter-group">
            <label htmlFor="date-from" className="admin-payments__filter-label">
              From Date
            </label>
            <input
              id="date-from"
              type="date"
              value={filters.date_from}
              onChange={(e) => setFilters({ ...filters, date_from: e.target.value })}
              className="admin-payments__filter admin-payments__filter--date"
            />
          </div>

          <div className="admin-payments__filter-group">
            <label htmlFor="date-to" className="admin-payments__filter-label">
              To Date
            </label>
            <input
              id="date-to"
              type="date"
              value={filters.date_to}
              onChange={(e) => setFilters({ ...filters, date_to: e.target.value })}
              className="admin-payments__filter admin-payments__filter--date"
            />
          </div>

          {hasActiveFilters && (
            <div className="admin-payments__filter-group admin-payments__filter-group--reset">
              <Button
                variant="secondary"
                size="medium"
                onClick={handleResetFilters}
                className="admin-payments__reset-btn"
              >
                Clear All Filters
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Completed Jobs Section */}
      {showCompletedJobs && jobsWithoutPayment.length > 0 && (
        <div className="admin-payments__completed-jobs">
          <h2 className="admin-payments__section-title">Completed Jobs Without Payment Requests</h2>
          <div className="admin-payments__jobs-list">
            {jobsWithoutPayment.map((job) => (
              <div key={job.id} className="admin-payments__job-card">
                <div className="admin-payments__job-info">
                  <div>
                    <h3 className="admin-payments__job-role">
                      {job.job_role?.job_role_name || job.job_role_name || 'N/A'}
                    </h3>
                    <p className="admin-payments__job-meta">
                      {formatDate(job.date || job.job_date)} • {job.employer?.email || job.employer_email || 'N/A'}
                    </p>
                  </div>
                  <div className="admin-payments__job-amount">
                    {formatCurrency(job.payment_amount || job.amount || 0)}
                  </div>
                </div>
                <Button
                  variant="primary"
                  size="small"
                  onClick={() => handleCreatePaymentRequest(job.id)}
                  disabled={createPaymentRequestMutation.isPending}
                  loading={createPaymentRequestMutation.isPending}
                  className="admin-payments__create-btn"
                >
                  Create Payment Request
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Payments List */}
      <div className="admin-payments__list">
        <h2 className="admin-payments__section-title">All Payments</h2>
        {!payments || payments.length === 0 ? (
          <div className="admin-payments__empty">
            <p>No payments found.</p>
          </div>
        ) : (
          <div className="admin-payments__table-container">
            <table className="admin-payments__table">
              <thead>
                <tr>
                  <th>Job Date</th>
                  <th>Job Role</th>
                  <th>Employer</th>
                  <th>Employee</th>
                  <th>Amount</th>
                  <th>Employer Status</th>
                  <th>Employee Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {payments.map((payment) => (
                  <React.Fragment key={payment.id}>
                    <tr 
                      className="admin-payments__row"
                      onClick={() => handleToggleExpand(payment.id)}
                    >
                      <td>{formatDate(payment.job_posting_detail?.date || payment.job_posting?.date || payment.job_date)}</td>
                      <td>
                        {payment.job_posting_detail?.job_role_detail?.job_role_name || 
                         payment.job_posting?.job_role_detail?.job_role_name ||
                         payment.job_role_name || 
                         'N/A'}
                      </td>
                      <td>{payment.employer_detail?.email || payment.job_posting_detail?.employer_email || payment.employer_email || 'N/A'}</td>
                      <td>{payment.employee_detail?.email || payment.employee_email || 'N/A'}</td>
                      <td className="admin-payments__amount">{formatCurrency(payment.total_amount || payment.amount)}</td>
                      <td>
                        <PaymentStatusBadge 
                          status={payment.employer_payment_status} 
                          type="employer"
                        />
                      </td>
                      <td>
                        <PaymentStatusBadge 
                          status={payment.employee_payout_status} 
                          type="employee"
                        />
                      </td>
                      <td>
                        <span className="admin-payments__expand-icon">
                          {expandedPaymentId === payment.id ? '▼' : '▶'}
                        </span>
                      </td>
                    </tr>
                    {expandedPaymentId === payment.id && (
                      <tr className="admin-payments__details-row">
                        <td colSpan="8" className="admin-payments__details-cell">
                          <div className="admin-payments__details-content">
                            <PaymentDetails paymentId={payment.id} />
                            <div className="admin-payments__actions-section">
                              <AdminPaymentActions 
                                payment={payment}
                                onSuccess={() => {
                                  // Refetch will happen automatically via React Query
                                }}
                              />
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPayments;

